import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import TopupFeePreview from "@/components/TopupFeePreview";

/**
 * Customer wallet balance card + inline "Add funds" dialog.
 *
 * Encapsulates the full top-up flow used by BBPS V2 wizard: fetches balance for
 * the given customer mobile, opens a dialog with gateway/channel pickers, shows
 * the live MDR + Settlement fee preview, and routes the user to the PG checkout
 * tab on submit.
 *
 * Props:
 *   mobile      : string  — customer mobile (10 digits)
 *   onChanged   : fn      — fired after a successful top-up dispatch (parent
 *                           can refresh its own state e.g. wallet balance again)
 *   hidden      : bool    — render nothing when true (e.g. in self-mode)
 *   testidPrefix: string  — for unique data-testids if multiple panels exist
 */
export default function CustomerWalletPanel({ mobile, onChanged, hidden = false, testidPrefix = "cust-wallet" }) {
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupGateway, setTopupGateway] = useState("razorpay");
  const [topupChannel, setTopupChannel] = useState("");
  const [topupOptions, setTopupOptions] = useState({ gateways: [], channels: { manarecharge: [] } });
  const [topupOptionsLoaded, setTopupOptionsLoaded] = useState(false);
  const [topupSubmitting, setTopupSubmitting] = useState(false);

  const loadWallet = async () => {
    if (!mobile) return;
    setWalletLoading(true);
    try {
      const { data } = await api.get(`/customer-wallet/balance/${mobile}`);
      setWallet(data);
    } catch {
      setWallet(null);
    } finally {
      setWalletLoading(false);
    }
  };

  useEffect(() => { loadWallet(); /* eslint-disable-next-line */ }, [mobile]);

  // Lazy-load gateway list on dialog open
  useEffect(() => {
    if (!topupOpen || topupOptionsLoaded) return;
    (async () => {
      try {
        const { data } = await api.get("/wallet/topup-options");
        setTopupOptions(data || { gateways: [], channels: { manarecharge: [] } });
        const avail = (data?.gateways || []).filter((g) => g.available);
        if (avail.length > 0 && !avail.some((g) => g.id === topupGateway)) {
          setTopupGateway(avail[0].id);
          setTopupChannel("");
        }
      } catch {
        setTopupOptions({
          gateways: [
            { id: "razorpay", label: "Razorpay", available: true, supports_channel: false },
            { id: "cashfree", label: "Cashfree", available: true, supports_channel: false },
            { id: "manarecharge", label: "ManaRecharge", available: true, supports_channel: true },
            { id: "runpaisa", label: "Runpaisa", available: true, supports_channel: false },
          ],
          channels: { manarecharge: [] },
        });
      } finally {
        setTopupOptionsLoaded(true);
      }
    })();
  }, [topupOpen, topupOptionsLoaded, topupGateway]);

  const availableGateways = (topupOptions.gateways || []).filter((g) => g.available);
  const manaChannels = topupOptions.channels?.manarecharge || [];

  const submit = async () => {
    const amt = parseFloat(topupAmount);
    if (!amt || amt < 1) { toast.error("Enter a valid amount"); return; }
    setTopupSubmitting(true);
    try {
      const payload = { mobile, amount: amt, gateway: topupGateway };
      if (topupGateway === "manarecharge" && topupChannel) payload.pg = topupChannel;
      const { data } = await api.post("/customer-wallet/topup/order", payload);
      toast.success("Top-up order created. Customer pays at PG.");
      const checkoutUrl =
        data?.sdk_params?.checkout_url ||
        data?.sdk_params?.payment_link ||
        data?.sdk_params?.short_url ||
        data?.sdk_params?.redirect_url ||
        data?.sdk_params?.url || "";
      if (checkoutUrl) {
        window.open(checkoutUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Top-up created but the PG did not return a checkout URL. Check the topup record under wallet › transactions.");
      }
      setTopupOpen(false);
      setTopupAmount("");
      setTimeout(() => { loadWallet(); onChanged?.(); }, 4000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Top-up failed");
    } finally {
      setTopupSubmitting(false);
    }
  };

  if (hidden || !mobile) return null;

  return (
    <>
      <div data-testid={`${testidPrefix}-panel`} className="rounded-lg border border-primary/20 bg-primary/5 p-3 sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Customer wallet</div>
            <div className="mt-0.5 flex items-baseline gap-2 flex-wrap">
              <span className="font-display text-2xl font-bold tabular-nums" data-testid={`${testidPrefix}-balance`}>
                {walletLoading ? "…" : (wallet ? formatINR(wallet.balance) : "₹—")}
              </span>
              {wallet?.customer_name && (
                <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                  · {wallet.customer_name}
                </span>
              )}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            data-testid={`${testidPrefix}-add-funds`}
            onClick={() => setTopupOpen(true)}
            className="border-primary/40 text-primary hover:bg-primary/10"
          >
            + Add funds
          </Button>
        </div>
      </div>

      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent data-testid={`${testidPrefix}-topup-dialog`}>
          <DialogHeader>
            <DialogTitle>Add funds to customer wallet</DialogTitle>
            <DialogDescription>
              +91 {mobile} · current balance{" "}
              <span className="font-mono">{wallet ? formatINR(wallet.balance) : "—"}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Amount (₹)</Label>
              <Input
                data-testid={`${testidPrefix}-topup-amount`}
                type="number" min={1} step={1}
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                className="font-mono mt-1"
                placeholder="500"
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {[500, 1000, 2000, 5000].map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`${testidPrefix}-preset-${v}`}
                    onClick={() => setTopupAmount(String(v))}
                    className="text-[11px] px-2 py-1 rounded-full border border-border hover:bg-accent font-mono"
                  >
                    +₹{v}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">
                Gateway {!topupOptionsLoaded && <span className="text-muted-foreground">· loading…</span>}
              </Label>
              <select
                data-testid={`${testidPrefix}-gateway`}
                value={topupGateway}
                onChange={(e) => { setTopupGateway(e.target.value); setTopupChannel(""); }}
                disabled={!topupOptionsLoaded || availableGateways.length === 0}
                className="mt-1 w-full font-mono text-sm rounded-md border border-input bg-background px-2 py-1.5 disabled:opacity-60"
              >
                {availableGateways.length === 0 && topupOptionsLoaded && (
                  <option value="">No gateways configured — contact admin</option>
                )}
                {availableGateways.map((g) => (
                  <option key={g.id} value={g.id}>{g.label}</option>
                ))}
              </select>
              {topupOptionsLoaded && availableGateways.length === 0 && (
                <p className="mt-1 text-[11px] text-destructive">
                  No PG has both credentials AND a top-up fee rule configured. Ask admin to enable a gateway in <code className="font-mono">/admin/topup-fees</code>.
                </p>
              )}
            </div>
            {topupGateway === "manarecharge" && (() => {
              const meta = (topupOptions.gateways || []).find((g) => g.id === "manarecharge");
              if (!meta?.supports_channel) return null;
              return (
                <div>
                  <Label className="text-xs">
                    Channel <span className="text-muted-foreground">(optional — uses gateway default if blank)</span>
                  </Label>
                  <select
                    data-testid={`${testidPrefix}-channel`}
                    value={topupChannel}
                    onChange={(e) => setTopupChannel(e.target.value)}
                    className="mt-1 w-full font-mono text-sm rounded-md border border-input bg-background px-2 py-1.5"
                  >
                    <option value="">Default (admin-configured)</option>
                    {manaChannels.map((ch) => (
                      <option key={ch.id} value={ch.id} disabled={ch.active === false}>
                        {ch.name || ch.id}{ch.active === false ? " (inactive)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })()}
            <TopupFeePreview
              amount={topupAmount}
              gateway={topupGateway}
              channel={topupChannel}
              testidPrefix={`${testidPrefix}-fee`}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopupOpen(false)} disabled={topupSubmitting}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={topupSubmitting || !parseFloat(topupAmount)}
              data-testid={`${testidPrefix}-submit`}
            >
              {topupSubmitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Create top-up order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
